import { useMutationState } from '@tanstack/react-query'
import type { PropsWithChildren } from 'react'
import { Navigate, useLocation } from 'router-v6'
import { getDefaultRoute } from 'v2/configs/routes'
import { SuspenseLoader } from 'v2/shared/components/suspense-loader'
import { onboardingMutationKeys } from 'v2/shared/utils/query-key-management'
import { useOnboardingCompleted } from './onboarding-completed'

export type OnboardingGateResult = 'loading' | 'redirect' | 'leave' | 'render'

// Stamped on the gate's redirect so a deliberate visit to /onboarding is distinguishable.
export const ONBOARDING_REDIRECT_STATE = { auto: true } as const

// Pure so it can be pinned directly in tests — useProfileQuery is a leaked-mock
// victim (CLAUDE.md failure mode 17), so component-level tests of the gate are
// unreliable in the full suite.
export const resolveOnboardingGate = ({
  onboardingCompleted,
  isLoading,
  pathname,
  autoRedirected,
  submittedHere,
}: {
  onboardingCompleted: boolean | undefined
  isLoading: boolean
  pathname: string
  // Location carries ONBOARDING_REDIRECT_STATE.
  autoRedirected: boolean
  // This tab's own completion mutation is pending or succeeded.
  submittedHere: boolean
}): OnboardingGateResult => {
  if (isLoading) return 'loading'
  const onWizard = pathname === '/onboarding'
  // `undefined` (no org yet, profile error) fails open — the API enforces the
  // real state, and locking the app on absent data hides it entirely.
  if (onboardingCompleted === false && !onWizard) return 'redirect'
  // Onboarded elsewhere while parked here by the gate (SER-6449): leave. A deliberate
  // visit carries no state and is never moved — re-running the wizard is supported. The
  // submitting tab navigates to its own destination via finishOnboarding, never via this.
  if (onboardingCompleted === true && onWizard && autoRedirected && !submittedHere) {
    return 'leave'
  }
  return 'render'
}

// Post-signup gate: a brand-new org (backend `onboarding_completed` still
// false) is routed straight into the guided onboarding wizard at /onboarding,
// which completes the backend flag on finish.
//
// This must WRAP the layout, not sit beside it: as a sibling that rendered
// null while the profile loaded, the dashboard painted for the whole
// /accounts/profile round-trip after 2FA and was then yanked to the wizard
// (EN-1108). Holding a loader until the FIRST load resolves (isLoading, never
// isFetching — a background refetch must not unmount the app) means the
// dashboard subtree never mounts before the redirect decision.
//
// `completed` is monotonic per tab (onboarding-completed.ts): a stale `false` can't bounce it.
export const OnboardingGate = ({ children }: PropsWithChildren) => {
  const { completed, isLoading } = useOnboardingCompleted()
  const { pathname, state } = useLocation()
  // Mutation cache, not render ordering: <Navigate> fires from an effect, which is late
  // enough to override finishOnboarding's own navigation (seen on dev, 2026-09-04).
  const submittedHere = useMutationState({
    filters: { mutationKey: onboardingMutationKeys().complete },
    select: (mutation) => mutation.state.status,
  }).some((status) => status === 'pending' || status === 'success')

  const decision = resolveOnboardingGate({
    onboardingCompleted: completed,
    isLoading,
    pathname,
    autoRedirected: (state as typeof ONBOARDING_REDIRECT_STATE | null)?.auto === true,
    submittedHere,
  })

  if (decision === 'loading') return <SuspenseLoader text="Fetching account details..." />
  if (decision === 'redirect') {
    return <Navigate to="/onboarding" replace state={ONBOARDING_REDIRECT_STATE} />
  }
  if (decision === 'leave') return <Navigate to={getDefaultRoute()} replace />
  return <>{children}</>
}
